import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import usePageMeta from "@/lib/usePageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Star, MapPin, CalendarDays, BadgeCheck, Info } from "lucide-react";
import { toast } from "sonner";
import CarGallery from "@/components/car/CarGallery";
import CarSpecsRow from "@/components/car/CarSpecsRow";
import CarCardBadges from "@/components/car/CarCardBadges";
import CarTcoChip from "@/components/car/CarTcoChip";
import CarPriceCalendar from "@/components/car/CarPriceCalendar";
import CarDestinationsBlock from "@/components/car/CarDestinationsBlock";
import CarFuelPolicy from "@/components/car/CarFuelPolicy";
import CarMileagePolicy from "@/components/car/CarMileagePolicy";
import CarInsurancePlansList from "@/components/car/CarInsurancePlansList";
import CarAddOnsList from "@/components/car/CarAddOnsList";
import CarReviewsList from "@/components/car/CarReviewsList";
import DuplicateBookingDialog from "@/components/car/DuplicateBookingDialog";
import VendorGoogleReviews from "@/components/car/VendorGoogleReviews";
import BookingWidget from "@/components/car/BookingWidget";
import SocialProofBadge from "@/components/car/SocialProofBadge";
import StayLongerWidget from "@/components/car/StayLongerWidget";
import BookEarlierWidget from "@/components/car/BookEarlierWidget";
import SimilarInVendorWidget from "@/components/car/SimilarInVendorWidget";
import useSocialProof from "@/lib/useSocialProof";
import useCarHeartbeat from "@/lib/useCarHeartbeat";
import CarRentalInfoStrip from "@/components/car/CarRentalInfoStrip";
import CarFeatureChips from "@/components/car/CarFeatureChips";
import ImportantInfoPopup from "@/components/car/ImportantInfoPopup";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { X as XIcon } from "lucide-react";
import { toLocalIso, fromLocalIso } from "@/lib/localDate";
import ModifySearchPill from "@/components/car/ModifySearchPill";
import { recordVendorVisit } from "@/lib/recentVendors";
import VendorNameLink from "@/components/common/VendorNameLink";

// Iter 67: pull the search engine's pre-selected dates/locations out of the URL so
// the booking widget arrives pre-filled. Locations only get applied if the car
// actually offers that pickup/dropoff option — otherwise we fall back to the car's
// first listed venue. Dates are timezone-safe via `toLocalIso`; times are read from
// the ISO timestamp's LOCAL hours/minutes so the renter sees what they originally
// picked on the hero stepper, not a UTC-shifted value.
function _parseTimeFromIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CarDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [car, setCar] = useState(null);
  const [reviews, setReviews] = useState([]);
  // Iter 69 — single-car social-proof signal for the booking widget.
  // Uses the same shared hook as Browse so caching is automatic.
  const { getProofFor } = useSocialProof(car ? [car.id] : []);
  const proof = car ? getProofFor(car.id) : null;
  // Iter 69 — broadcast presence so the renter shows up in OTHER tabs'
  // "X watching now" count for this car. Idempotent, anonymous, every ~25s.
  useCarHeartbeat(car?.id);
  const [quote, setQuote] = useState(null);
  const [bookedRanges, setBookedRanges] = useState([]);
  // Iter 67en+++++++ — renter's own active bookings across vendors.
  // Painted amber on the date picker so they spot a conflict early.
  const [myBookedRanges, setMyBookedRanges] = useState([]);
  // Iter 67dh — popup for Important info + Full rental terms (modal).
  const [infoOpen, setInfoOpen] = useState(false);
  // Seed start/end/pickup/dropoff/times from the URL (forwarded from the Home/Browse
  // search) so the widget is pre-filled. Empty when the user lands here directly.
  const [start, setStart] = useState(() => toLocalIso(searchParams.get("start_date")));
  const [end, setEnd] = useState(() => toLocalIso(searchParams.get("end_date")));
  const [pickup, setPickup] = useState(() => searchParams.get("pickup_location") || "");
  const [dropoff, setDropoff] = useState(() => searchParams.get("dropoff_location") || "");
  const [pickupTime, setPickupTime] = useState(() => _parseTimeFromIso(searchParams.get("start_date")) || "12:00");
  const [returnTime, setReturnTime] = useState(() => _parseTimeFromIso(searchParams.get("end_date")) || "12:00");
  const [planId, setPlanId] = useState("");
  const [addOnIds, setAddOnIds] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({});
  const [secondDriver, setSecondDriver] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [vendorPaymentMethod, setVendorPaymentMethod] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  // Iter 67en+++++++ — duplicate-booking guard. Set when the
  // pre-checkout overlap API returns ≥1 overlapping booking; the
  // dialog stays mounted and shows the soft warning. "Book anyway"
  // clears the dialog and re-runs `book()` with the override flag.
  const [duplicateOverlap, setDuplicateOverlap] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Iter 67ea — localized title/description; falls back to brand when car is null.
  usePageMeta(
    car ? "meta.carDetail.title" : null,
    car ? "meta.carDetail.description" : null,
    car ? { car: car.title } : undefined,
  );

  // Prefill driver details from the logged-in customer's saved profile so they don't have
  // to retype on every booking. The fields stay fully editable per booking.
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    setCustomerDetails((prev) => ({
      full_name: prev.full_name || user.name || "",
      email: prev.email || user.email || "",
      phone: prev.phone || user.phone || "",
      address: prev.address || user.address || "",
      country: prev.country || user.country || "",
      id_number: prev.id_number || user.id_number || "",
      license_number: prev.license_number || user.license_number || "",
      license_expiry: prev.license_expiry || user.license_expiry || "",
      date_of_birth: prev.date_of_birth || user.date_of_birth || "",
    }));
  }, [user]);

  useEffect(() => {
    api.get(`/cars/${id}`).then((r) => {
      setCar(r.data);
      const pl = r.data.pickup_locations || [];
      const dl = r.data.dropoff_locations || [];
      // Honour the URL-forwarded selection only when this car actually offers that
      // pickup/dropoff venue — otherwise drop back to the car's first listed option
      // so the shadcn Select always has a valid value to display.
      setPickup((p) => (p && pl.includes(p)) ? p : (pl[0] || ""));
      setDropoff((d) => (d && dl.includes(d)) ? d : (dl[0] || ""));
      // Iter 67ad — every car-detail visit records the host so the
      // "Recently viewed hosts" strip surfaces them next time the renter
      // hits the home page or browse listing.
      const v = r.data.vendor || {};
      if (r.data.vendor_id) {
        recordVendorVisit({
          id: r.data.vendor_id,
          name: v.business_name || v.name || "Host",
          logo: v.business_logo || null,
        });
      }
    }).catch((e) => console.error(e));
    api.get(`/reviews/car/${id}`).then((r) => setReviews(r.data)).catch((e) => console.error(e));
    api.get(`/cars/${id}/booked-dates`).then((r) => setBookedRanges(r.data || [])).catch((e) => console.error(e));
  }, [id]);

  // Iter 67en+++++++ — fetch the customer's own active booked date ranges
  // (across ALL vendors) once on mount, then transform into the shape
  // the date picker's `dateInRanges()` helper expects (`{start_date,end_date}`).
  useEffect(() => {
    if (!user || user.role !== "customer") {
      setMyBookedRanges([]);
      return;
    }
    api.get("/customer/bookings/my-booked-ranges")
      .then(({ data }) => {
        const ranges = (data?.ranges || []).map((r) => ({
          start_date: r.start,
          end_date: r.end,
        }));
        setMyBookedRanges(ranges);
      })
      .catch(() => setMyBookedRanges([]));
  }, [user]);

  // Front-end pre-check: do selected dates overlap an existing booking?
  const conflict = (() => {
    if (!start || !end) return null;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (!(e > s)) return null;
    return bookedRanges.find((r) => {
      const rs = new Date(r.start_date).getTime();
      const re = new Date(r.end_date).getTime();
      return rs < e && re > s;
    }) || null;
  })();

  // Merge a YYYY-MM-DD date string with a "HH:MM" time into a single ISO timestamp.
  // We treat the renter-chosen time as local (vendor's local hours) and convert to UTC
  // for storage — `new Date('YYYY-MM-DDTHH:MM:00')` already does this correctly because
  // omitting a TZ marker means "local".
  const composeDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const t = (timeStr || "12:00").trim();
    return new Date(`${dateStr.slice(0, 10)}T${t}:00`).toISOString();
  };

  // Live quote: re-fetched whenever a relevant input changes.
  useEffect(() => {
    if (!start || !end) { setQuote(null); return; }
    let cancelled = false;
    api.post(`/cars/${id}/quote`, {
      start_date: composeDateTime(start, pickupTime),
      end_date: composeDateTime(end, returnTime),
      pickup_location: pickup || undefined,
      dropoff_location: dropoff || undefined,
      insurance_plan_id: planId || undefined,
      add_on_ids: addOnIds,
    }).then((r) => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [id, start, end, pickup, dropoff, planId, addOnIds, pickupTime, returnTime]);

  // Iter 67dk — when the renter picks a pickup date, snap the drop-off
  // date to the earliest allowed value (pickup + max(minimum_rental_days, 1)).
  // Only auto-fills/bumps when `end` is empty or strictly before the new
  // minimum, so the renter is never overridden once they've chosen a longer
  // rental. Skipping booked-range collisions here — the date picker's
  // `disabled` predicate prevents them at selection time anyway.
  useEffect(() => {
    if (!start || !car) return;
    const minDays = Math.max(1, Number(car.minimum_rental_days) || 1);
    const startDt = fromLocalIso(start);
    if (!startDt) return;
    const minEnd = new Date(startDt);
    minEnd.setDate(minEnd.getDate() + minDays);
    const minEndIso = toLocalIso(minEnd);
    const currentEnd = fromLocalIso(end);
    if (!currentEnd || currentEnd.getTime() < minEnd.getTime()) {
      setEnd(minEndIso);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, car?.minimum_rental_days]);

  const days = quote?.days || 0;

  // Iter 69 — vendor-controlled conversion-widget visibility. Each flag
  // defaults to true; vendors can disable any/all via Vendor → Settings.
  const widgetFlags = car?.vendor?.conversion_widgets || {};
  const showStayLonger      = widgetFlags.stay_longer !== false;
  const showBookEarlier     = widgetFlags.book_earlier !== false;
  const showSimilarInVendor = widgetFlags.similar_in_vendor !== false;

  // Iter 69 — StayLonger widget extension handler. Receives `+N days` from
  // the widget and shifts the local end-date forward by N days. Keeps the
  // existing setEnd contract (local-ISO string) intact so the rest of the
  // booking pipeline re-quotes via the existing useEffect on `end`.
  const extendBy = (extraDays) => {
    const currentEnd = fromLocalIso(end);
    if (!currentEnd || !extraDays) return;
    const shifted = new Date(currentEnd);
    shifted.setDate(shifted.getDate() + extraDays);
    setEnd(toLocalIso(shifted));
    // Soft scroll-to-summary so the renter sees the updated total without
    // hunting for it (matters more on mobile where the bottom-sheet closes
    // on tap).
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_e) { /* SSR / no window */ }
  };

  // Iter 69 — BookEarlier widget handler. Shifts BOTH start + end back by
  // N×7 days, preserving trip duration. Uses the same setStart/setEnd
  // contract so the existing /quote useEffect re-fires automatically.
  const shiftEarlierBy = (weeksEarlier) => {
    const s = fromLocalIso(start);
    const e = fromLocalIso(end);
    if (!s || !e || !weeksEarlier) return;
    const shiftDays = weeksEarlier * 7;
    const newStart = new Date(s);
    const newEnd = new Date(e);
    newStart.setDate(newStart.getDate() - shiftDays);
    newEnd.setDate(newEnd.getDate() - shiftDays);
    setStart(toLocalIso(newStart));
    setEnd(toLocalIso(newEnd));
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_e) { /* SSR / no window */ }
  };

  const book = async (force = false) => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Only customer accounts can book"); return; }
    if (days <= 0) { toast.error("Pick valid dates"); return; }
    if (!pickup || !dropoff) { toast.error("Choose pickup and drop-off locations"); return; }
    // Required driver-identity validation (matches backend). Friendlier here so the user
    // sees the toast without losing the page state to a 400.
    const required = [
      ["full_name", "Full name"],
      ["email", "Email"],
      ["phone", "Phone or WhatsApp"],
      ["address", "Full address"],
      ["country", "Country"],
      ["id_number", "Passport or ID number"],
      ["license_number", "Driver licence number"],
      ["license_expiry", "Licence expiry date"],
    ];
    const missing = required.filter(([k]) => !((customerDetails[k] || "").trim?.() || customerDetails[k])).map(([, label]) => label);
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(", ")}`);
      return;
    }
    // Licence must be valid through the entire rental.
    if (customerDetails.license_expiry && end) {
      const exp = new Date(customerDetails.license_expiry);
      const endDt = new Date(end);
      if (!isNaN(exp) && exp < endDt) {
        toast.error(`Your driver licence expires on ${customerDetails.license_expiry}, before the rental end date. Please renew it before booking.`);
        return;
      }
    }
    // Renter must explicitly tick the "I accept the host's policies" checkbox.
    if (!termsAccepted) {
      toast.error("Please tick the box confirming you accept the host's Terms, Cancellation and Privacy policies.");
      return;
    }

    // Iter 67en+++++++ — Soft duplicate-booking guard. Only ask the
    // backend when (a) we're not bypassing via "Book anyway" and (b)
    // we have a vendor_id to scope by. Failure modes: any error from
    // the check falls through to the booking call silently — the
    // user must never be blocked from booking just because the
    // diagnostic endpoint flaked.
    if (!force && car?.vendor_id) {
      try {
        const { data: chk } = await api.post(
          "/customer/bookings/check-overlap",
          { vendor_id: car.vendor_id, start_date: start, end_date: end },
        );
        if (chk?.has_overlap && (chk.overlapping?.length || 0) > 0) {
          setDuplicateOverlap(chk.overlapping);
          return;  // dialog handles next step
        }
      } catch { /* fail-open — proceed to book */ }
    }

    setLoading(true);
    try {
      // Iter 67bs — pick up any partner-referral code stored in localStorage
      // (set by the Browse page when the URL had `?ref=...`). If present, send
      // it on; the backend validates it belongs to this car's vendor.
      const referralCode = (() => {
        try {
          // Iter 67fg — read new key first, fall back to legacy key for users
          // who acquired the referral cookie before the RentalCar.al rebrand.
          const v = (localStorage.getItem("rentalcar_partner_ref") || localStorage.getItem("drivenest_partner_ref") || "").trim();
          return v || undefined;
        }
        catch { return undefined; }
      })();
      // Iter 67fa — attribute the booking back to the vendor's widget / micro-site
      // when the customer arrived via `/v/{slug}` or `/embed/v/{slug}`. Stamped on
      // the booking doc so analytics can split widget vs organic conversions.
      const sourceVendorSlug = (() => {
        try { return (localStorage.getItem("rentalcar:source_vendor_slug") || "").trim() || undefined; }
        catch { return undefined; }
      })();
      // Iter 67eh — pick up any affiliate_id the search-widget snippet
      // baked into the URL/localStorage. Persisted in localStorage by the
      // embed page so it survives car-detail → checkout navigation.
      const affiliateId = (() => {
        try { return (localStorage.getItem("rentalcar:affiliate_id") || "").trim() || undefined; }
        catch { return undefined; }
      })();
      const { data } = await api.post("/bookings/checkout", {
        car_id: id,
        start_date: composeDateTime(start, pickupTime),
        end_date: composeDateTime(end, returnTime),
        origin_url: window.location.origin,
        pickup_location: pickup,
        dropoff_location: dropoff,
        pickup_time: pickupTime,
        return_time: returnTime,
        insurance_plan_id: planId || undefined,
        add_on_ids: addOnIds,
        customer_details: customerDetails,
        second_driver: Object.values(secondDriver).some(Boolean) ? secondDriver : undefined,
        terms_accepted: true,
        vendor_payment_method: vendorPaymentMethod || undefined,
        redeem_points: Number(redeemPoints) > 0 ? Number(redeemPoints) : undefined,
        referral_code: referralCode,
        source_vendor_slug: sourceVendorSlug,
        affiliate_id: affiliateId,
      });
      // Iter 62: when the vendor is on the monthly subscription plan with 0 platform
      // commission, Stripe is bypassed entirely (offline_only=true, url=null). Redirect
      // straight to the success page with the offline-marker session id.
      if (data?.offline_only) {
        window.location.href = `/booking/success?session_id=${encodeURIComponent(data.session_id)}`;
        return;
      }
      // Iter 71b — Route through the reservation-hold checkout page. It
      // shows the 20-minute countdown timer + booking summary, then
      // launches Stripe when the customer clicks "Pay now". We stash the
      // Stripe URL in sessionStorage so it survives refresh + Stripe
      // redirect-back scenarios (the backend hold-status API doesn't
      // store the Stripe URL — only the checkout endpoint returns it).
      try {
        sessionStorage.setItem(`rentalcar:checkout_url:${data.booking_id}`, data.url);
      } catch { /* storage disabled — user can still complete on this tab */ }
      window.location.href = `/checkout/${encodeURIComponent(data.booking_id)}`;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  if (!car) return <div className="min-h-screen"><Header /><div className="max-w-7xl mx-auto p-12 text-neutral-500">Loading…</div></div>;

  return (
    <div>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <ModifySearchPill />
        <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mb-2 flex items-baseline flex-wrap gap-2" data-testid="car-detail-title">
          <span>{car.make} {car.model}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-xs font-semibold uppercase tracking-wider">or similar</span>
        </h1>
        <div className="flex items-center gap-3 text-sm text-neutral-600 mb-6">
          <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-neutral-900 text-neutral-900" /> {car.rating > 0 ? car.rating.toFixed(1) : "New"} ({car.review_count})</span>
          <span>·</span>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(car.vendor?.office_address || car.location || "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 px-2.5 py-1 font-semibold text-xs transition"
            title={car.vendor?.office_address
              ? `Open the host's office (${car.vendor.office_address}) in Google Maps`
              : `Open ${car.location} in Google Maps`}
            data-testid="car-location-link"
          >
            <MapPin className="h-3.5 w-3.5" /> {car.location}
          </a>
        </div>

        <CarGallery images={car.images || []} />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 mt-12">
          <div>
            <div className="pb-8 border-b border-border">
              <div className="flex items-center gap-3 mb-4" data-testid="vendor-host-chip">
                {car.vendor?.business_logo ? (
                  // Iter 67bo — host logos render with `object-contain` (same
                  // treatment as the "Trusted hosts" carousel on the home page)
                  // so the brand mark is preserved instead of being cropped
                  // into a circular avatar.
                  <div className="h-12 px-3 rounded-xl bg-white border border-border flex items-center justify-center shrink-0">
                    <img
                      src={car.vendor.business_logo.startsWith("http") ? car.vendor.business_logo : fileUrl(car.vendor.business_logo)}
                      alt={car.vendor?.business_name || "Vendor logo"}
                      className="max-h-9 max-w-[120px] w-auto object-contain"
                      data-testid="vendor-host-logo"
                    />
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded-full bg-neutral-900 text-white flex items-center justify-center font-medium">
                    {car.vendor?.business_name?.[0] || car.vendor?.name?.[0] || "H"}
                  </div>
                )}
                <div>
                  <div className="font-medium">
                    Hosted by{" "}
                    <VendorNameLink
                      vendor={car.vendor}
                      className="text-blue-600 hover:underline font-medium"
                      testIdSuffix="cardetail-host"
                    />
                  </div>
                  <div className="text-sm text-neutral-500 flex items-center gap-2 flex-wrap mt-1">
                    {/* Iter 67bq — "Trusted RentalCar.al host" promoted from plain
                        text to a visual emerald badge. Stronger trust cue at
                        the booking decision-point without re-exposing the
                        email (still removed in Iter 67bo). */}
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[11px] font-bold uppercase tracking-wider"
                      data-testid="vendor-host-trusted-badge"
                    >
                      <BadgeCheck className="h-3 w-3" /> Trusted RentalCar.al host
                    </span>
                    {car.vendor?.id && (
                      <button
                        type="button"
                        onClick={() => setInfoOpen(true)}
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded"
                        data-testid="vendor-host-policies-link"
                      >
                        <Info className="h-3.5 w-3.5" /> Important info
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <CarSpecsRow car={car} />
            <CarCardBadges car={car} className="mt-4" testIdPrefix="cardetail-perks" />
            {/* Iter 67bf — Total Cost of Ownership chip. Self-hides for anyone
                who isn't the owning vendor or an admin. */}
            <div className="mt-3"><CarTcoChip carId={car.id} /></div>

            <div className="py-8 border-b border-border">
              <h3 className="font-display text-xl font-medium mb-3">About this car</h3>
              <p className="text-neutral-700 leading-relaxed">{car.description}</p>
              {(() => {
                // Iter 67aw — promote the host's cancellation window into the
                // description block so renters reading the prose see the
                // commitment in full sentence form, not just the chip above.
                const raw = car.free_cancellation_hours;
                const hours = (raw === null || raw === undefined) ? 48 : Number(raw);
                if (!hours || hours <= 0) {
                  return (
                    <div
                      className="mt-4 text-sm text-neutral-700 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 ring-1 ring-amber-200"
                      data-testid="cardetail-cancellation-line"
                    >
                      <span className="font-semibold text-amber-900">No free cancellation.</span>
                      <span className="text-amber-800">Booking is non-refundable once confirmed.</span>
                    </div>
                  );
                }
                const label = hours >= 72 && hours % 24 === 0
                  ? (hours / 24 === 1 ? "1 day" : `${hours / 24} days`)
                  : `${hours} hours`;
                return (
                  <div
                    className="mt-4 text-sm text-neutral-700 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 ring-1 ring-violet-200"
                    data-testid="cardetail-cancellation-line"
                  >
                    <span className="font-semibold text-violet-900">Free cancellation</span>
                    <span className="text-violet-800">up to {label} before pickup — full refund, no questions asked.</span>
                  </div>
                );
              })()}
              {car.deposit > 0 && (
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100" data-testid="deposit-banner">
                  Refundable security deposit: <Money amount={car.deposit} className="font-semibold" />
                </div>
              )}
              {car.features?.length > 0 && (
                <div className="mt-6">
                  <CarFeatureChips features={car.features} />
                </div>
              )}
            </div>

            <CarDestinationsBlock car={car} />
            <CarFuelPolicy policy={car.fuel_policy} />
            <CarMileagePolicy
              policy={car.mileage_policy}
              limit={car.mileage_limit_km_per_day}
              rate={car.mileage_extra_km_rate}
            />
            {(Number(car.minimum_rental_days) > 1 || Number(car.maximum_rental_days) > 0 || Number(car.minimum_driver_age) > 0 || car.deposit_payment_method || (Array.isArray(car.pickup_payment_methods_resolved) && car.pickup_payment_methods_resolved.length > 0)) && (
              <CarRentalInfoStrip car={car} />
            )}
            <CarInsurancePlansList plans={car.insurance_plans || []} />
            <CarAddOnsList addOns={car.add_ons || []} />
            {/* Iter 67bj — 2-month price calendar showing the effective €/day
                from base / seasonal / date-range rules. */}
            <CarPriceCalendar carId={car.id} />
            <CarReviewsList reviews={reviews} carVendorId={car?.vendor_id} />
            {/* Iter 67ab — augment in-platform reviews with the vendor's live
                Google Business Profile reviews. Component self-hides when the
                feature is disabled or the vendor hasn't supplied a Place ID. */}
            <VendorGoogleReviews vendorId={car.vendor_id} />
          </div>

          <aside className="hidden lg:block">
            {/* Iter 69 — social-proof banner above the booking widget.
                Either "Booked Nm ago" or "X watching now". Single-fetch
                via the same hook used by Browse. Falls back gracefully
                when the API errs or returns nothing applicable. */}
            {proof && (
              <div className="mb-3 flex justify-end" data-testid="cardetail-social-proof">
                <SocialProofBadge proof={proof} size="lg" />
              </div>
            )}
            {/* Iter 69 — "Stay one more day" conversion widget. Re-fetches as
                start/end change; clicking a row bumps `end` via `extendBy`.
                Self-hides when the vendor disabled the toggle. */}
            <div className="mb-3 space-y-3">
              {showStayLonger && (
                <StayLongerWidget
                  carId={id}
                  startIso={start ? composeDateTime(start, pickupTime) : null}
                  endIso={end ? composeDateTime(end, returnTime) : null}
                  onExtend={extendBy}
                />
              )}
              {/* Iter 69 — inverse "Book earlier and save" anchor-pricing nudge.
                  Self-hides when no shifted window saves the renter money OR
                  when the vendor disabled the toggle. */}
              {showBookEarlier && (
                <BookEarlierWidget
                  carId={id}
                  startIso={start ? composeDateTime(start, pickupTime) : null}
                  endIso={end ? composeDateTime(end, returnTime) : null}
                  onShift={shiftEarlierBy}
                />
              )}
              {/* Iter 69 — "Same fleet, save €X" upsell/downsell. Keeps the
                  customer inside the host vendor's funnel while showing
                  cheaper alternatives. Self-hides when nothing helps OR
                  when the vendor disabled the toggle. */}
              {showSimilarInVendor && (
                <SimilarInVendorWidget
                  carId={id}
                  startIso={start ? composeDateTime(start, pickupTime) : null}
                  endIso={end ? composeDateTime(end, returnTime) : null}
                />
              )}
            </div>
            <BookingWidget
              car={car}
              pickup={pickup} setPickup={setPickup}
              dropoff={dropoff} setDropoff={setDropoff}
              start={start} setStart={setStart}
              end={end} setEnd={setEnd}
              pickupTime={pickupTime} setPickupTime={setPickupTime}
              returnTime={returnTime} setReturnTime={setReturnTime}
              planId={planId} setPlanId={setPlanId}
              addOnIds={addOnIds} setAddOnIds={setAddOnIds}
              customerDetails={customerDetails} setCustomerDetails={setCustomerDetails}
              secondDriver={secondDriver} setSecondDriver={setSecondDriver}
              termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted}
              vendorPaymentMethod={vendorPaymentMethod} setVendorPaymentMethod={setVendorPaymentMethod}
              redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
              bookedRanges={bookedRanges}
              myBookedRanges={myBookedRanges}
              conflict={conflict}
              quote={quote}
              loading={loading}
              onBook={book}
            />
          </aside>
        </div>
      </div>

      {/*
        Iter 67ct — Mobile booking bottom-sheet.
        On `< lg`, the inline aside above is hidden so the reviews/gallery
        sections sit closer to the fold. In its place we render a fixed
        bottom CTA bar with the live quote total + "Book now" button that
        opens the same `BookingWidget` inside a Radix `Sheet`. Desktop is
        unchanged.

        The CTA bar uses `safe-area-inset-bottom` padding so it sits cleanly
        above iOS home indicators, and an extra spacer (`h-20 lg:hidden`)
        prevents the very last review from being eclipsed by the bar.
      */}
      <div className="h-20 lg:hidden" aria-hidden="true" />
      <div
        className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-gradient-to-r from-sky-50 via-violet-50 to-pink-50 border-t border-sky-200 shadow-[0_-6px_20px_-10px_rgba(99,102,241,0.45)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-booking-cta-bar"
      >
        <div className="px-4 py-3 flex items-center gap-3">
          <div
            className="flex-1 min-w-0 rounded-2xl bg-white/80 backdrop-blur ring-1 ring-sky-200 px-3 py-2 shadow-sm"
            data-testid="mobile-booking-cta-price-card"
          >
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-sky-700">
              {quote?.total ? "Estimated total" : "Daily rate"}
            </div>
            <div className="text-lg font-bold text-neutral-900 tabular-nums leading-tight" data-testid="mobile-booking-cta-price">
              <Money amount={quote?.total || car.price_per_day || 0} />
              {!quote?.total && <span className="ml-1 text-xs font-normal text-neutral-500">/ day</span>}
            </div>
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button
                size="lg"
                className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-bold px-6 shrink-0 shadow-md shadow-orange-200"
                data-testid="mobile-booking-cta-open"
              >
                Book now
              </Button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="h-[92vh] p-0 rounded-t-3xl overflow-hidden flex flex-col"
              data-testid="mobile-booking-sheet"
            >
              <SheetHeader className="px-5 pt-5 pb-3 border-b border-border bg-white sticky top-0 z-10">
                <div className="flex items-center justify-between gap-3">
                  <SheetTitle className="text-left text-base">Book this car</SheetTitle>
                  <SheetClose asChild>
                    <button
                      type="button"
                      aria-label="Close"
                      className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-700 flex items-center justify-center transition shrink-0"
                      data-testid="mobile-booking-sheet-close"
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </SheetClose>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-4 pb-8">
                {/* Iter 69 — same StayLonger widget rendered above the
                    mobile booking widget so renters on mobile see the
                    conversion nudge before the dense booking form. */}
                <div className="mb-4 space-y-3">
                  {showStayLonger && (
                    <StayLongerWidget
                      carId={id}
                      startIso={start ? composeDateTime(start, pickupTime) : null}
                      endIso={end ? composeDateTime(end, returnTime) : null}
                      onExtend={extendBy}
                    />
                  )}
                  {showBookEarlier && (
                    <BookEarlierWidget
                      carId={id}
                      startIso={start ? composeDateTime(start, pickupTime) : null}
                      endIso={end ? composeDateTime(end, returnTime) : null}
                      onShift={shiftEarlierBy}
                    />
                  )}
                  {showSimilarInVendor && (
                    <SimilarInVendorWidget
                      carId={id}
                      startIso={start ? composeDateTime(start, pickupTime) : null}
                      endIso={end ? composeDateTime(end, returnTime) : null}
                    />
                  )}
                </div>
                <BookingWidget
                  car={car}
                  pickup={pickup} setPickup={setPickup}
                  dropoff={dropoff} setDropoff={setDropoff}
                  start={start} setStart={setStart}
                  end={end} setEnd={setEnd}
                  pickupTime={pickupTime} setPickupTime={setPickupTime}
                  returnTime={returnTime} setReturnTime={setReturnTime}
                  planId={planId} setPlanId={setPlanId}
                  addOnIds={addOnIds} setAddOnIds={setAddOnIds}
                  customerDetails={customerDetails} setCustomerDetails={setCustomerDetails}
                  secondDriver={secondDriver} setSecondDriver={setSecondDriver}
                  termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted}
                  vendorPaymentMethod={vendorPaymentMethod} setVendorPaymentMethod={setVendorPaymentMethod}
                  redeemPoints={redeemPoints} setRedeemPoints={setRedeemPoints}
                  bookedRanges={bookedRanges}
                  myBookedRanges={myBookedRanges}
                  conflict={conflict}
                  quote={quote}
                  loading={loading}
                  onBook={book}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <Footer />
      {/* Iter 67em — chat bubble now mounted globally in App.js
          (<GlobalChatBubble />) and derives carId from the route, so the
          per-page mount is no longer needed here. */}
      {/* Iter 67dh — Important info + Full rental terms popup. */}
      {car && (
        <ImportantInfoPopup open={infoOpen} onOpenChange={setInfoOpen} car={car} />
      )}
      {/* Iter 67en+++++++ — Soft duplicate-booking warning. */}
      <DuplicateBookingDialog
        open={!!duplicateOverlap}
        overlapping={duplicateOverlap || []}
        onClose={() => setDuplicateOverlap(null)}
        onContinue={() => {
          setDuplicateOverlap(null);
          // re-fire book() with force=true so the same checks are skipped this time.
          book(true);
        }}
      />
    </div>
  );
}
